/* ÉLANE theme.js — lightweight, dependency-free */
document.documentElement.classList.remove('no-js');

(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const trapFocus = (container) => {
    const focusable = qsa('a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])', container)
      .filter(el => !el.disabled && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------- Header scroll state ---------- */
  const header = qs('[data-header]');
  if (header) {
    const solidify = () => {
      if (window.scrollY > 40) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    solidify();
    document.addEventListener('scroll', solidify, { passive: true });
  }

  /* ---------- Drawers (cart / mobile nav / filters) ---------- */
  function openDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    const overlay = qs('[data-overlay]');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.dataset.releaseFocus = 'pending';
    const release = trapFocus(drawer);
    drawer._releaseFocus = release;
  }
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const anyOpen = qsa('.drawer.is-open').length > 0;
    if (!anyOpen) {
      const overlay = qs('[data-overlay]');
      if (overlay) overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    if (drawer._releaseFocus) drawer._releaseFocus();
  }
  document.addEventListener('click', (e) => {
    const openTrigger = e.target.closest('[data-open-drawer]');
    if (openTrigger) {
      e.preventDefault();
      openDrawer(qs(openTrigger.dataset.openDrawer));
    }
    const closeTrigger = e.target.closest('[data-close-drawer]');
    if (closeTrigger) {
      const drawer = closeTrigger.closest('.drawer');
      closeDrawer(drawer || qs(closeTrigger.dataset.closeDrawer));
    }
    if (e.target.matches('[data-overlay]')) {
      qsa('.drawer.is-open').forEach(closeDrawer);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') qsa('.drawer.is-open').forEach(closeDrawer);
  });

  /* Mobile nav submenu disclosure */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.mobile-nav-disclosure__toggle');
    if (!toggle) return;
    const sub = toggle.nextElementSibling;
    if (sub) {
      const willOpen = !sub.classList.contains('is-open');
      sub.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(willOpen));
    }
  });

  /* ---------- Reveal on scroll ---------- */
  if (window.ELANE_SETTINGS && window.ELANE_SETTINGS.animationsReveal === false) {
    qsa('[data-reveal]').forEach(el => el.classList.add('is-visible'));
  } else if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    qsa('[data-reveal]').forEach(el => io.observe(el));
  } else {
    qsa('[data-reveal]').forEach(el => el.classList.add('is-visible'));
  }

  /* ---------- Announcement bar rotation ---------- */
  qsa('[data-announcement-bar]').forEach((bar) => {
    const slides = qsa('.announcement-bar__slide', bar);
    if (slides.length < 2) return;
    let i = 0;
    setInterval(() => {
      slides[i].classList.remove('is-active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('is-active');
    }, 4500);
  });

  /* ---------- Quantity stepper ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrapper = btn.closest('.qty-stepper');
    const input = qs('input', wrapper);
    let val = parseInt(input.value, 10) || 1;
    val = btn.dataset.qtyChange === 'increase' ? val + 1 : Math.max(1, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart (AJAX) ---------- */
  const CartAPI = {
    async add(formData) {
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
      if (!res.ok) throw await res.json();
      return res.json();
    },
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },
    async change(line, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      return res.json();
    }
  };
  window.ElaneCart = CartAPI;

  async function refreshCartDrawer() {
    try {
      const res = await fetch(window.location.pathname + '?section_id=cart-drawer');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('#cart-drawer');
      const current = qs('#cart-drawer');
      if (newDrawer && current) current.innerHTML = newDrawer.innerHTML;
    } catch (err) { /* silent — cart still functions via redirect fallback */ }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = qs('[type="submit"]', form);
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = submitBtn.dataset.loadingText || 'Adding…'; }
    try {
      await CartAPI.add(new FormData(form));
      await refreshCartDrawer();
      await updateCartCount();
      const cartType = document.body.dataset.cartType || 'drawer';
      if (cartType === 'page') { window.location.href = '/cart'; return; }
      openDrawer(qs('#cart-drawer'));
    } catch (err) {
      const msgEl = qs('[data-form-error]', form);
      if (msgEl) msgEl.textContent = (err && err.description) || 'This item could not be added right now.';
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
    }
  });

  async function onCartLineMutated() {
    if (qs('[data-cart-page-root]')) {
      window.location.reload();
      return;
    }
    await refreshCartDrawer();
    await updateCartCount();
  }

  async function updateCartCount() {
    const cart = await CartAPI.get();
    qsa('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.classList.toggle('hidden', cart.item_count === 0); });
    return cart;
  }

  document.addEventListener('click', async (e) => {
    const stepBtn = e.target.closest('[data-cart-line-change]');
    if (stepBtn) {
      const line = stepBtn.dataset.line;
      const delta = stepBtn.dataset.cartLineChange === 'increase' ? 1 : -1;
      const currentQty = parseInt(stepBtn.closest('.cart-drawer__item, .cart-line').querySelector('[data-line-qty]').textContent, 10);
      await CartAPI.change(line, Math.max(0, currentQty + delta));
      await onCartLineMutated();
    }
    const removeBtn = e.target.closest('[data-cart-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      await CartAPI.change(removeBtn.dataset.line, 0);
      await onCartLineMutated();
    }
  });

  /* ---------- Quick add (single-variant products) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add-simple]');
    if (!btn) return;
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      const fd = new FormData();
      fd.append('id', btn.dataset.variantId);
      fd.append('quantity', 1);
      await CartAPI.add(fd);
      await refreshCartDrawer();
      await updateCartCount();
      openDrawer(qs('#cart-drawer'));
    } catch (err) {
      btn.textContent = 'Unavailable';
      setTimeout(() => { btn.textContent = original; }, 1800);
    } finally {
      btn.disabled = false;
      if (btn.textContent === 'Adding…') btn.textContent = original;
    }
  });

  /* ---------- Product variant selection ---------- */
  qsa('[data-product-form]').forEach((form) => {
    const dataEl = qs('[data-product-json]', form.closest('[data-product-root]') || document);
    if (!dataEl) return;
    let productData;
    try { productData = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const optionInputs = qsa('[data-option-index]', form);
    const idInput = qs('[name="id"]', form);
    // Price/compare elements may also exist outside data-product-root (e.g. the mobile
    // sticky ATC bar), so update every instance on the page rather than a single scoped one.
    const priceEls = qsa('[data-product-price]');
    const compareEls = qsa('[data-product-compare-price]');
    const submitBtn = qs('[type="submit"]', form);
    const stockEl = qs('[data-stock-status]', form.closest('[data-product-root]'));

    function getSelectedOptions() {
      const groups = {};
      optionInputs.forEach(input => {
        if (input.type === 'radio' && !input.checked) return;
        groups[input.dataset.optionIndex] = input.value;
      });
      return Object.keys(groups).sort().map(k => groups[k]);
    }

    function findVariant() {
      const selected = getSelectedOptions();
      return productData.variants.find(v => v.options.every((opt, idx) => opt === selected[idx]));
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function render() {
      const variant = findVariant();
      qsa('.variant-pill, .variant-swatch', form).forEach(el => el.classList.remove('is-selected'));
      optionInputs.forEach(input => {
        if ((input.type === 'radio' && input.checked) || input.classList.contains('is-checked')) {
          const label = form.querySelector(`[data-option-value="${input.dataset.optionIndex}:${CSS.escape(input.value)}"]`);
          if (label) label.classList.add('is-selected');
        }
      });
      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Unavailable'; }
        return;
      }
      idInput.value = variant.id;
      priceEls.forEach(el => { el.textContent = formatMoney(variant.price); });
      compareEls.forEach(el => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = formatMoney(variant.compare_at_price);
          el.classList.remove('hidden');
        } else el.classList.add('hidden');
      });
      if (submitBtn) {
        if (!variant.available) { submitBtn.disabled = true; submitBtn.textContent = 'Sold out'; }
        else { submitBtn.disabled = false; submitBtn.textContent = submitBtn.dataset.defaultText || 'Add to cart'; }
      }
      if (stockEl) {
        stockEl.classList.remove('stock-status--low', 'stock-status--out');
        if (!variant.available) { stockEl.classList.add('stock-status--out'); stockEl.querySelector('span').textContent = 'Sold out'; }
        else if (variant.inventory_quantity !== undefined && variant.inventory_quantity <= 5 && variant.inventory_quantity > 0) { stockEl.classList.add('stock-status--low'); stockEl.querySelector('span').textContent = 'Only ' + variant.inventory_quantity + ' left'; }
        else { stockEl.querySelector('span').textContent = 'In stock'; }
      }
      const media = productData.variantImages && productData.variantImages[variant.id];
      if (media) {
        const mainImg = qs('[data-gallery-main-img]');
        if (mainImg) mainImg.src = media;
      }
      history.replaceState({}, '', `?variant=${variant.id}`);
    }

    form.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-option-value]');
      if (!pill) return;
      const [index, value] = pill.dataset.optionValue.split(':');
      const input = form.querySelector(`[data-option-index="${index}"][value="${CSS.escape(value)}"]`);
      if (input) { input.checked = true; render(); }
    });
    render();
  });

  /* ---------- Gallery thumbnails ---------- */
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('.product-gallery__thumb');
    if (!thumb) return;
    const gallery = thumb.closest('[data-gallery]');
    qsa('.product-gallery__thumb', gallery).forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    const main = qs('[data-gallery-main-img]', gallery);
    if (main && thumb.dataset.fullSrc) main.src = thumb.dataset.fullSrc;
  });

  /* ---------- Sticky mobile ATC ---------- */
  const mainAtc = qs('[data-main-atc]');
  const stickyAtc = qs('[data-sticky-atc]');
  if (mainAtc && stickyAtc && 'IntersectionObserver' in window) {
    const io2 = new IntersectionObserver(([entry]) => {
      stickyAtc.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    io2.observe(mainAtc);
  }

  /* ---------- Before / After slider ---------- */
  qsa('[data-ba-slider]').forEach((slider) => {
    const before = qs('.ba-slider__before', slider);
    const handle = qs('.ba-slider__handle', slider);
    function setPos(pct) {
      pct = Math.min(100, Math.max(0, pct));
      before.style.width = pct + '%';
      handle.style.left = pct + '%';
      handle.setAttribute('aria-valuenow', Math.round(pct));
    }
    function fromClientX(clientX) {
      const rect = slider.getBoundingClientRect();
      setPos(((clientX - rect.left) / rect.width) * 100);
    }
    let dragging = false;
    handle.addEventListener('pointerdown', () => { dragging = true; });
    window.addEventListener('pointerup', () => { dragging = false; });
    window.addEventListener('pointermove', (e) => { if (dragging) fromClientX(e.clientX); });
    slider.addEventListener('click', (e) => fromClientX(e.clientX));
    handle.addEventListener('keydown', (e) => {
      const current = parseFloat(before.style.width) || 50;
      if (e.key === 'ArrowLeft') setPos(current - 5);
      if (e.key === 'ArrowRight') setPos(current + 5);
    });
    setPos(50);
  });

  /* ---------- Shop the look hotspots ---------- */
  document.addEventListener('click', (e) => {
    const hotspot = e.target.closest('.hotspot');
    if (hotspot) {
      const isActive = hotspot.classList.contains('is-active');
      qsa('.hotspot').forEach(h => h.classList.remove('is-active'));
      if (!isActive) hotspot.classList.add('is-active');
      return;
    }
    if (!e.target.closest('.hotspot-card')) qsa('.hotspot').forEach(h => h.classList.remove('is-active'));
  });

  /* ---------- Hair Finder quiz ---------- */
  qsa('[data-hair-finder]').forEach((quiz) => {
    const steps = qsa('.hair-finder__step', quiz);
    const progressBar = qs('.hair-finder__progress-bar', quiz);
    const answers = {};
    let current = 0;

    function show(index) {
      steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
      if (progressBar) progressBar.style.width = ((index) / (steps.length - 1)) * 100 + '%';
      const stepCounter = qs('[data-step-counter]', quiz);
      if (stepCounter) stepCounter.textContent = `Step ${Math.min(index + 1, steps.length - 1)} of ${steps.length - 1}`;
    }

    quiz.addEventListener('click', (e) => {
      const option = e.target.closest('.hair-finder__option');
      if (option) {
        const group = option.closest('.hair-finder__step');
        qsa('.hair-finder__option', group).forEach(o => o.classList.remove('is-selected'));
        option.classList.add('is-selected');
        answers[group.dataset.field] = option.dataset.value;
        setTimeout(() => { if (current < steps.length - 1) { current++; show(current); } }, 220);
        return;
      }
      const next = e.target.closest('[data-quiz-next]');
      if (next && current < steps.length - 1) { current++; show(current); }
      const back = e.target.closest('[data-quiz-back]');
      if (back && current > 0) { current--; show(current); }
      const restart = e.target.closest('[data-quiz-restart]');
      if (restart) { current = 0; Object.keys(answers).forEach(k => delete answers[k]); qsa('.hair-finder__option', quiz).forEach(o => o.classList.remove('is-selected')); show(current); }
      const finish = e.target.closest('[data-quiz-finish]');
      if (finish) {
        current = steps.length - 1;
        show(current);
        renderResult();
      }
    });

    function renderResult() {
      const resultLink = qs('[data-quiz-result-link]', quiz);
      const resultSummary = qs('[data-quiz-result-summary]', quiz);
      const map = quiz.dataset.resultMap ? JSON.parse(quiz.dataset.resultMap) : {};
      const key = answers.texture || 'straight';
      const dest = map[key] || quiz.dataset.defaultCollection || '/collections/all';
      if (resultLink) resultLink.href = dest;
      if (resultSummary) {
        resultSummary.textContent = `${answers.look || ''} · ${answers.texture || ''} · ${answers.length || ''} · ${answers.install || ''}`.replace(/^ · | · $/g, '');
      }
    }

    show(0);
  });

  /* ---------- Predictive search ---------- */
  const searchInput = qs('[data-predictive-search-input]');
  if (searchInput) {
    const resultsEl = qs('[data-predictive-search-results]');
    const modal = qs('[data-search-modal]');
    let debounceTimer;
    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const term = searchInput.value.trim();
      if (term.length < 2) { if (resultsEl) resultsEl.innerHTML = ''; return; }
      debounceTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor,tag`);
          const data = await res.json();
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (resultsEl) {
            resultsEl.innerHTML = products.length ? products.map(p => `
              <a class="search-result" href="${p.url}">
                <span class="search-result__media">${p.image ? `<img src="${p.image}" alt="" loading="lazy">` : '<span class="placeholder-art"></span>'}</span>
                <span>
                  <span style="display:block;font-size:14px;">${p.title}</span>
                  ${p.price ? `<span class="muted" style="font-size:13px;">${p.price}</span>` : ''}
                </span>
              </a>`).join('') : `<p class="muted" style="padding: var(--space-4) 0;">No results for "${term}"</p>`;
          }
        } catch (err) { /* ignore */ }
      }, 250);
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-open-search]')) { modal.classList.add('is-open'); setTimeout(() => searchInput.focus(), 60); }
      if (e.target.closest('[data-close-search]') || e.target === modal) { modal.classList.remove('is-open'); }
    });
  }

  /* ---------- Accordions (native <details>) enhancement: close siblings optionally ---------- */
  qsa('[data-accordion-group]').forEach((group) => {
    if (group.dataset.accordionGroup !== 'exclusive') return;
    qsa('details', group).forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) qsa('details', group).forEach(o => { if (o !== d) o.open = false; });
      });
    });
  });

})();
